import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Phone, Mail, MapPin } from 'lucide-react';
import { contactInfo } from '../mockData';
import { NAV_ITEMS } from '../lib/navigation';
import { createStaggerContainer, fadeInLeft, fadeInUp, scaleIn } from '../lib/animations';

const NAV_LINK_CLASSES = 'text-[#f5ebe5]/80 hover:text-white transition-colors duration-300 text-xs sm:text-sm';
const BODY_TEXT_CLASSES = 'text-[#f5ebe5]/80 text-xs sm:text-sm';

const legalLinks = [
  { to: '/mentions-legales', label: 'Mentions légales' },
  { to: '/politique-confidentialite', label: 'Politique de confidentialité' },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.18, delayChildren: 0.1 });

  const contactMethods = [
    {
      Icon: Phone,
      href: `tel:${contactInfo.phone.link}`,
      label: contactInfo.phone.display,
      className: NAV_LINK_CLASSES,
    },
    {
      Icon: Mail,
      href: `mailto:${contactInfo.email.address}`,
      label: contactInfo.email.address,
      className: `${NAV_LINK_CLASSES} break-all`,
    },
  ];

  return (
    <motion.footer className="bg-[#a5400e] text-white py-8 sm:py-12" initial={shouldReduceMotion ? undefined : { opacity: 0 }} animate={shouldReduceMotion ? undefined : { opacity: 1 }} transition={shouldReduceMotion ? undefined : { duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" variants={containerVariants}>
          <motion.div variants={fadeInUp}>
            <h3 className="text-xl sm:text-2xl font-serif mb-2">Siriane Belliard-Reynes</h3>
            <p className="text-[#f5ebe5]/80 text-sm mb-3 sm:mb-4">Neuro Thérapeute</p>
            <p className="text-[#f5ebe5]/70 text-xs sm:text-sm leading-relaxed">
              {"Accompagnement de bien-être en neuro thérapie, à l’écoute de vos ressentis et de votre histoire."}
            </p>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Navigation</h4>
            <motion.ul className="space-y-2" variants={createStaggerContainer({ staggerChildren: 0.08 })}>
              {NAV_ITEMS.map(({ href, label }) => (
                <motion.li key={href} variants={fadeInLeft}>
                  <a href={href} className={NAV_LINK_CLASSES}>
                    {label}
                  </a>
                </motion.li>
              ))}
              {legalLinks.map(({ to, label }) => (
                <motion.li key={to} variants={fadeInLeft}>
                  <Link to={to} className={NAV_LINK_CLASSES}>
                    {label}
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Contact</h4>
            <motion.ul className="space-y-2 sm:space-y-3" variants={createStaggerContainer({ staggerChildren: 0.1 })}>
              <motion.li className="flex items-start space-x-2" variants={scaleIn}>
                <MapPin size={16} className="mt-1 flex-shrink-0" />
                <div>
                  <p className={BODY_TEXT_CLASSES}>{contactInfo.address.street}</p>
                  <p className={BODY_TEXT_CLASSES}>{contactInfo.address.city}</p>
                </div>
              </motion.li>
              {contactMethods.map(({ Icon, href, label, className }) => (
                <motion.li key={href} className="flex items-start space-x-2" variants={scaleIn}>
                  <Icon size={16} className="mt-1 flex-shrink-0" />
                  <a href={href} className={className}>
                    {label}
                  </a>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </motion.div>

        <motion.div className="border-t border-white/20 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center" variants={fadeInUp}>
          <p className="text-[#f5ebe5]/60 text-xs sm:text-sm">
            &copy; {currentYear} Siriane Belliard-Reynes - Neuro Thérapeute. Tous droits réservés.
          </p>
          <p className="text-[#f5ebe5]/50 text-xs mt-2">Consultations sur rendez-vous uniquement</p>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;
